import type { NewsItem } from '../../api/client'
import { Refs } from '../common/Refs'
import { Verknuepfungen } from '../common/Verknuepfungen'
import type { Verknuepfung } from '../../hooks/useVerknuepft'
import { Bewertung, bewertungsTon } from './Bewertung'
import { nurUhr, zeitVoll } from './newsZeit'
import s from './NewsDetail.module.scss'

/** Was das erste Ziel über die Meldung sagt: Titel, Rohstoff, Makro, Branche … */
function kategorie(item: NewsItem): string {
  const vorzeichen = item.sentiment > 0 ? 'Positiv' : item.sentiment < 0 ? 'Negativ' : 'Neutral'
  const art = item.targets[0]?.kind
  const bereich = art === 'isin' ? 'Titel' : art === 'comm' ? 'Rohstoffe'
    : art === 'geo' ? 'Makro' : art === 'ind' ? 'Branche'
    : art === 'crypt' ? 'Krypto' : art === 'mkt' ? 'Markt' : 'Allgemein'
  return `${vorzeichen} · ${bereich}`
}

/**
 * Das Meldungsdetail — der rechte Teil der Master-Detail-Ansicht.
 *
 * Oben das Blättern, darunter die Meldung als Artikel: Bewertung, Kategorie,
 * Zeit, Überschrift in der Serife, Text. Der farbige Streifen links trägt das
 * Vorzeichen. Darunter vier Felder: Betrifft, Verknüpft, Quelle, Sentiment.
 */
export function NewsDetail({ item, position, onVorherige, onNaechste,
                             verknuepft, verknuepftLaedt }: {
  item: NewsItem
  /** „3 von 12" oder „ASML Holding · 1 von 2". */
  position: string
  onVorherige: () => void
  onNaechste: () => void
  verknuepft: Verknuepfung[]
  verknuepftLaedt: boolean
}) {
  const quelle = item.sources[0]
  const staerke = Math.min(3, Math.abs(item.sentiment))
  const ton = bewertungsTon(item.sentiment)

  return (
    <div className={s.detail}>
      <div className={s.blaettern}>
        <span>{position}</span>
        <span className={s.knoepfe}>
          <button type="button" onClick={onVorherige}>‹ vorherige</button>
          <button type="button" onClick={onNaechste}>nächste ›</button>
        </span>
      </div>

      <article className={s.artikel}>
        <div className={s.kopf}>
          <div className={s.streifen} data-ton={ton} />
          <div className={s.inhalt}>
            <div className={s.zeile}>
              <Bewertung wert={item.sentiment} gross />
              <span className={s.kategorie}>{kategorie(item)}</span>
              <span className={s.zeit}>{zeitVoll(item.published_at)}</span>
            </div>
            <h2 className={s.titel}>{item.headline}</h2>
            {/* `pre-line`: Absätze im Text bleiben Absätze. */}
            <p className={s.text}>{item.body}</p>
          </div>
        </div>

        <div className={s.felder}>
          <div className={s.feld}>
            <div className={s.feldTitel}>Betrifft</div>
            <Refs refs={item.targets} leer="ohne Bezug" />
          </div>
          <div className={s.feld}>
            <div className={s.feldTitel}>Verknüpft</div>
            <Verknuepfungen eintraege={verknuepft} laedt={verknuepftLaedt} />
          </div>
          <div className={s.feld}>
            <div className={s.feldTitel}>Quelle</div>
            <a href={item.source_url} target="_blank" rel="noreferrer" className={s.quelleLink}>
              {item.source_url.replace(/^https?:\/\//, '')}
            </a>
            <div className={s.quelleMeta}>
              {quelle?.publisher ?? quelle?.title ?? 'Quelle'} · abgerufen {nurUhr(item.created)}
            </div>
          </div>
          <div className={s.feld}>
            <div className={s.feldTitel}>Sentiment</div>
            <div className={s.sentiment}>
              <span className={s.sentimentWert} data-ton={ton}>
                {item.sentiment > 0 ? `+${item.sentiment}` : item.sentiment < 0 ? `−${Math.abs(item.sentiment)}` : '0'}
              </span>
              <span className={s.balken} aria-hidden="true">
                {[1, 2, 3].map((stufe) => (
                  <span key={stufe} data-ton={stufe <= staerke ? ton : 'leer'} />
                ))}
              </span>
              <span className={s.skala}>Skala −3 bis +3</span>
            </div>
          </div>
        </div>
      </article>
    </div>
  )
}
